import { MediaHandles } from './interface';

// Module-level media handles — survive component remounts within the same browser tab so the
// tab-share display stream stays alive across in-app navigation.
const mediaHandles: MediaHandles = {
  displayStream: null,
  micStream: null,
  audioCtx: null,
  mixedStream: null,
  recorder: null,
  chunks: [],
  recordingBlob: null,
  sentMsgIds: new Set()
};

export const getMedia = (): MediaHandles => mediaHandles;

export const resetMediaState = (): void => {
  mediaHandles.recordingBlob = null;
  mediaHandles.chunks = [];
  mediaHandles.recorder = null;
};

export const hasActiveDisplayStream = (): boolean =>
  !!mediaHandles.displayStream?.getAudioTracks().some(t => t.readyState === 'live');

export const stopRecording = (discard = false): void => {
  const rec = mediaHandles.recorder;
  if (!rec || rec.state !== 'recording') return;
  if (discard) {
    rec.ondataavailable = null;
    rec.onstop = null;
  }
  rec.stop();
  if (discard) {
    mediaHandles.chunks = [];
    mediaHandles.recordingBlob = null;
    mediaHandles.recorder = null;
  }
};

export const releaseAudioOnly = (): void => {
  stopRecording();
  mediaHandles.micStream?.getTracks().forEach(t => t.stop());
  try {
    mediaHandles.audioCtx?.close();
  } catch (_) {
    /* ignore */
  }
  mediaHandles.micStream = null;
  mediaHandles.audioCtx = null;
  mediaHandles.mixedStream = null;
};

export const setupAudioMix = async (
  displayStream: MediaStream,
  onDisplayStreamEnded: () => void
): Promise<void> => {
  const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
  const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
  const ctx = new AudioCtxClass();
  const dest = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(displayStream).connect(dest);
  ctx.createMediaStreamSource(ms).connect(dest);

  displayStream.getAudioTracks()[0].onended = () => {
    releaseAudioOnly();
    mediaHandles.displayStream = null;
    onDisplayStreamEnded();
  };

  mediaHandles.micStream = ms;
  mediaHandles.audioCtx = ctx;
  mediaHandles.mixedStream = dest.stream;
};

export const beginRecording = (onComplete: (blob: Blob) => void): void => {
  const mixed = mediaHandles.mixedStream;
  if (!mixed || mediaHandles.recorder?.state === 'recording') return;

  const mimeType = MediaRecorder.isTypeSupported('audio/mp4')
    ? 'audio/mp4'
    : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
    ? 'audio/webm;codecs=opus'
    : 'audio/webm';

  mediaHandles.chunks = [];
  const rec = new MediaRecorder(mixed, { mimeType });

  rec.ondataavailable = e => {
    if (e.data?.size) mediaHandles.chunks.push(e.data);
  };
  rec.onstop = () => {
    const blob = new Blob(mediaHandles.chunks, { type: mimeType });
    mediaHandles.recordingBlob = blob;
    mediaHandles.chunks = [];
    mediaHandles.recorder = null;
    onComplete(blob);
  };

  rec.start(1000);
  mediaHandles.recorder = rec;
};

export const sendExtensionMessage = (type: string, data: Record<string, unknown> = {}): void => {
  const msgId = `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  mediaHandles.sentMsgIds.add(msgId);
  window.postMessage({ type, data, _lhMsgId: msgId }, '*');
};

// Fully release everything (audio + display stream + recording state).
// Used when a call is cancelled / errored / explicitly reset.
export const releaseAll = (): void => {
  releaseAudioOnly();
  mediaHandles.displayStream?.getTracks().forEach(t => t.stop());
  mediaHandles.displayStream = null;
  mediaHandles.recordingBlob = null;
  mediaHandles.chunks = [];
  mediaHandles.recorder = null;
};
